/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress } from '@mui/material';
import React from 'react';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { AlertBanner } from '@opengeoweb/shared';
import type { TafConfig, TafType } from '../../types';
import { useTafTranslation } from '../../utils/i18n';
import { useTafConfig } from '../../utils/taf-api/hooks';

interface TafConfigWrapperContext {
  tafConfig?: TafConfig;
}

export const TafConfigContext: React.Context<TafConfigWrapperContext> =
  React.createContext<TafConfigWrapperContext>({
    tafConfig: null!,
  });

export const useTafConfigContext = (): Partial<TafConfigWrapperContext> =>
  React.useContext(TafConfigContext);

interface TafConfigWrapperProps {
  children?: React.ReactNode;
  type?: TafType;
}

const TafConfigWrapper: React.FC<TafConfigWrapperProps> = ({
  children,
  type = 'TAF',
}): React.JSX.Element => {
  const { t } = useTafTranslation();
  const [errorMessage, setErrorMessage] = React.useState<{
    title: string;
    info?: string;
  } | null>(null);
  const ERROR_CONFIG = t('error-config');
  const ERROR_CONFIG_MESSAGE = t('error-config-message');
  const { isLoggedIn } = useAuthenticationContext();
  const ERROR_AUTH = t('error-auth');
  const ERROR_AUTH_MESSAGE = t('error-auth-message');

  const {
    isFetching,
    error: tafConfigError,
    data: tafConfig,
    refetch: fetchConfig,
  } = useTafConfig(type);

  const onFetchConfig = async (): Promise<void> => {
    await fetchConfig();
  };

  React.useEffect(() => {
    if (!isLoggedIn) {
      setErrorMessage({
        title: ERROR_AUTH,
        info: ERROR_AUTH_MESSAGE,
      });
    } else if (tafConfigError) {
      setErrorMessage({
        title: ERROR_CONFIG,
        info: tafConfigError.message,
      });
    } else {
      setErrorMessage(null);
    }
  }, [
    t,
    isLoggedIn,
    tafConfigError,
    ERROR_CONFIG,
    ERROR_AUTH,
    ERROR_AUTH_MESSAGE,
  ]);

  const contextValue = React.useMemo(
    () => ({
      tafConfig,
    }),
    [tafConfig],
  );

  if (isLoggedIn && isFetching) {
    return (
      <LinearProgress
        color="secondary"
        data-testid="tafconfig-loading-bar"
        sx={{ marginBottom: '-4px' }}
      />
    );
  }

  if (errorMessage || !tafConfig) {
    return (
      <AlertBanner
        actionButtonProps={
          !errorMessage || errorMessage?.title !== ERROR_AUTH
            ? {
                title: t('error-title-retry'),
                onClick: onFetchConfig,
              }
            : undefined
        }
        data-testid="error-message"
        info={errorMessage ? errorMessage.info : ERROR_CONFIG_MESSAGE}
        severity="error"
        title={errorMessage ? errorMessage.title : ERROR_CONFIG}
      />
    );
  }

  return (
    <TafConfigContext.Provider value={contextValue}>
      {children}
    </TafConfigContext.Provider>
  );
};

export default TafConfigWrapper;
